import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Loader2 } from "lucide-react";
import useAxios from "../../../../../../hooks/useAxios";
import { API_DATASETS_METRICS } from "../../../../../../shared/lib/apiEndpointsResolver";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../../../shared/components/dialog/Dialog";
import UnexpectedBackendError from "../../../../../../shared/components/UnexpectedBackendError";
import { Label } from "../../../../../../shared/components/label/Label";
import { Input } from "../../../../../../shared/components/input/Input";
import OptionsDropdown from "./OptionsDropdown";
import { Textarea } from "../../../../../../shared/components/textarea/Textarea";
import { Button } from "../../../../../../shared/components/button/Button";

const metricFormInitialState = {
  code: "",
  columnName: "",
  name: { en: "", es: "" },
  description: { en: "", es: "" },
  aggregationFunction: "sum",
};

const AddMetricDialog = ({
  datasetId,
  aggregationFunctions,
  isMetricDialogOpen,
  setIsMetricDialogOpen,
  onAddMetricSuccessfully,
  checkMetricCodeDuplicated,
  checkMetricColumnNameDuplicated,
}) => {
  const { t } = useTranslation();
  const axios = useAxios();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [metricForm, setMetricForm] = useState(metricFormInitialState);
  const [unexpectedError, setUnexpectedError] = useState(false);

  const validateMetricForm = (form) => {
    const errors = {};

    if (!form.code.trim()) {
      errors.code = t("datasetsCentre.datasets.metrics.codeRequired");
    } else if (checkMetricCodeDuplicated(form.code.trim())) {
      errors.code = t("datasetsCentre.datasets.metrics.codeExists");
    }

    if (!form.columnName.trim()) {
      errors.columnName = t(
        "datasetsCentre.datasets.metrics.columnNameRequired",
      );
    } else if (
      checkMetricColumnNameDuplicated(
        form.columnName.trim(),
        form.aggregationFunction.trim(),
      )
    ) {
      errors.columnName = t("datasetsCentre.datasets.metrics.columnNameExists");
    }

    if (!form.name.en.trim()) {
      errors.nameEn = t("datasetsCentre.datasets.metrics.nameEnRequired");
    }

    if (!form.name.es.trim()) {
      errors.nameEs = t("datasetsCentre.datasets.metrics.nameEsRequired");
    }

    if (!form.description.en.trim()) {
      errors.descriptionEn = t(
        "datasetsCentre.datasets.metrics.descriptionEnRequired",
      );
    }

    if (!form.description.es.trim()) {
      errors.descriptionEs = t(
        "datasetsCentre.datasets.metrics.descriptionEsRequired",
      );
    }

    return errors;
  };

  const handleAddMetric = async () => {
    const errors = validateMetricForm(metricForm);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await axios.post(
        API_DATASETS_METRICS(datasetId),
        metricForm,
      );

      if (result.status === 200) {
        onAddMetricSuccessfully(result.data);
        closeDialog();
      } else {
        console.error(result.data);
        setUnexpectedError(true);
      }
    } catch (error) {
      console.error(error);
      setUnexpectedError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeDialog = () => {
    setIsMetricDialogOpen(false);
    setMetricForm(metricFormInitialState);
    setFormErrors({});
  };

  return (
    <Dialog open={isMetricDialogOpen} onOpenChange={setIsMetricDialogOpen}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t("datasetsCentre.datasets.metrics.addTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("datasetsCentre.datasets.metrics.addDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {unexpectedError && <UnexpectedBackendError />}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dim-code">
                {t("datasetsCentre.common.code")} *
              </Label>
              <Input
                id="dim-code"
                value={metricForm.code}
                onChange={(e) =>
                  setMetricForm((prev) => ({
                    ...prev,
                    code: e.target.value,
                  }))
                }
                placeholder="dimension_code"
                className={formErrors.code ? "border-destructive" : ""}
              />
              {formErrors.code && (
                <p className="text-sm text-destructive">{formErrors.code}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="dim-column">
                {t("datasetsCentre.common.columnName")} *
              </Label>
              <Input
                id="dim-column"
                value={metricForm.columnName}
                onChange={(e) =>
                  setMetricForm((prev) => ({
                    ...prev,
                    columnName: e.target.value,
                  }))
                }
                placeholder="column_name"
                className={formErrors.columnName ? "border-destructive" : ""}
              />
              {formErrors.columnName && (
                <p className="text-sm text-destructive">
                  {formErrors.columnName}
                </p>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="dim-datatype">
              {t("datasetsCentre.common.aggregationFunction")} *
            </Label>
            <OptionsDropdown
              value={metricForm.aggregationFunction}
              onValueChange={(value) =>
                setMetricForm((prev) => ({
                  ...prev,
                  aggregationFunction: value,
                }))
              }
              items={aggregationFunctions}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dim-name-en">
                {t("datasetsCentre.common.nameEn")} *
              </Label>
              <Input
                id="dim-name-en"
                value={metricForm.name.en}
                onChange={(e) =>
                  setMetricForm((prev) => ({
                    ...prev,
                    name: { ...prev.name, en: e.target.value },
                  }))
                }
                placeholder={t(
                  "datasetsCentre.datasets.metrics.addNameEnPlaceholder",
                )}
                className={formErrors.nameEn ? "border-destructive" : ""}
              />
              {formErrors.nameEn && (
                <p className="text-sm text-destructive">{formErrors.nameEn}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="dim-name-es">
                {t("datasetsCentre.common.nameEs")} *
              </Label>
              <Input
                id="dim-name-es"
                value={metricForm.name.es}
                onChange={(e) =>
                  setMetricForm((prev) => ({
                    ...prev,
                    name: { ...prev.name, es: e.target.value },
                  }))
                }
                placeholder={t(
                  "datasetsCentre.datasets.metrics.addNameEsPlaceholder",
                )}
                className={formErrors.nameEs ? "border-destructive" : ""}
              />
              {formErrors.nameEs && (
                <p className="text-sm text-destructive">{formErrors.nameEs}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dim-desc-en">
                {t("datasetsCentre.common.descriptionEn")} *
              </Label>
              <Textarea
                id="dim-desc-en"
                value={metricForm?.description?.en}
                onChange={(e) =>
                  setMetricForm((prev) => ({
                    ...prev,
                    description: { ...prev.description, en: e.target.value },
                  }))
                }
                placeholder={t(
                  "datasetsCentre.datasets.metrics.addDescriptionEnPlaceholder",
                )}
                rows={3}
              />
              {formErrors.descriptionEn && (
                <p className="text-sm text-destructive">
                  {formErrors.descriptionEn}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="dim-desc-es">
                {t("datasetsCentre.common.descriptionEs")} *
              </Label>
              <Textarea
                id="dim-desc-es"
                value={metricForm.description.es}
                onChange={(e) =>
                  setMetricForm((prev) => ({
                    ...prev,
                    description: { ...prev.description, es: e.target.value },
                  }))
                }
                placeholder={t(
                  "datasetsCentre.datasets.metrics.addDescriptionEsPlaceholder",
                )}
                rows={3}
              />
              {formErrors.descriptionEs && (
                <p className="text-sm text-destructive">
                  {formErrors.descriptionEs}
                </p>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={closeDialog}
            disabled={isSubmitting}
          >
            {t("datasetsCentre.common.cancel")}
          </Button>
          <Button onClick={handleAddMetric} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t("datasetsCentre.common.adding")}
              </>
            ) : (
              t("datasetsCentre.datasets.metrics.addButton")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddMetricDialog;
